import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'

/**
 * Full-screen image viewer.
 *
 * A thin specialisation of Radix Dialog: same focus trap / ESC /
 * portal as our regular `Dialog`, but the overlay is fully
 * opaque (modulo opacity), the content fills the viewport, and
 * the only widget on top is a close button.
 *
 * Why a dedicated component
 * -------------------------
 * The mission detail page lets the admin tap a signature or a
 * receipt photo to see it bigger. The regular `Dialog` is sized
 * `max-w-lg` and shows our usual close button + chrome — wrong
 * for "look at this image full screen".
 *
 * We deliberately don't ship zoom / pan. The receipt photos are
 * already 4032×3024 phone snaps and a CSS `object-contain` at
 * 90vh is enough to read the supervisor's signature. If real
 * users ask for zoom we'll wire it then.
 */
interface ImageLightboxProps {
  src: string
  alt: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ImageLightbox({
  src,
  alt,
  open,
  onOpenChange,
}: ImageLightboxProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        {/* Heavier overlay than the regular Dialog so the page
            chrome behind disappears visually. */}
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-black/85',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0',
          )}
        />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-0 z-50',
            'flex items-center justify-center p-4 sm:p-12',
            // Tapping the backdrop closes the lightbox — wired
            // on the wrapper, not the image, so a tap on the
            // photo itself does NOT dismiss (avoids accidental
            // close when zooming-pinching on touch screens).
          )}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) onOpenChange(false)
          }}
        >
          {/* Visually-hidden title for screen readers — Radix
              requires a title element inside Dialog.Content. */}
          <DialogPrimitive.Title className="sr-only">
            {alt}
          </DialogPrimitive.Title>
          <img
            src={src}
            alt={alt}
            className="max-w-full max-h-full object-contain rounded-md shadow-2xl"
          />
          <DialogPrimitive.Close
            aria-label="Fermer"
            className={cn(
              'absolute top-4 right-4',
              'inline-flex items-center justify-center',
              'h-10 w-10 rounded-full bg-white/10 text-white',
              'hover:bg-white/20 transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white',
            )}
          >
            <X size={20} aria-hidden="true" />
          </DialogPrimitive.Close>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
